/**
 * Which plan a focused upgrade offers, and how the offer is worded.
 *
 * Every upgrade CTA used to open the full plan grid. When the plan is already
 * known (a feature gate knows the cheapest tier that has the feature; a trial
 * knows the plan being trialled; "Change plan" means the next tier up), the
 * DirectUpgradeSheet offers that one plan instead, and the grid is one tap
 * away ("Compare all plans").
 *
 * Prices are never written here: rows come from /api/billing/upgrade-context
 * (subscription_plans), copy from orderPlanCatalog. Pure, so it is unit tested.
 */
import type { PlanBase } from './orderFlow';
import {
  formatUsd,
  intervalForPlan,
  isAnnualOnly,
  planAt,
  planCopy,
  savingsPerYear,
  type BillingInterval,
} from './orderPlanCatalog';
import { FEATURE_LABELS, PLAN_TIERS, requiredPlanFor, type GatedFeature, type PlanTier } from './featureGate';

/** The bits of an upgrade-context plan row this file reads. */
export interface PlanRow {
  id: string;
  name: string;
  basePrice: string | number;
  billingInterval: string;
}

const TIER_ORDER: PlanTier[] = PLAN_TIERS.map((t) => t.tier);

export function tierRank(tier: PlanTier | null | undefined): number {
  return tier ? TIER_ORDER.indexOf(tier) : -1;
}

/** "Lite", "Starter Monthly", "Growth Yearly" -> tier. Null for anything else. */
export function tierOfPlanName(name: string | null | undefined): PlanTier | null {
  const base = String(name ?? '').replace(/ (Monthly|Yearly|Annual)$/i, '').trim().toLowerCase();
  return TIER_ORDER.find((t) => t === base) ?? null;
}

/** The tier a plan id is on, read from the rows (ids are not trusted for meaning). */
export function tierOfPlanId(rows: PlanRow[] | null | undefined, planId: string | null | undefined): PlanTier | null {
  if (!planId) return null;
  const row = (rows ?? []).find((r) => r.id === planId);
  if (row) return tierOfPlanName(row.name);
  // Canonical monthly ids are named after their tier (see featureGate PLAN_TIERS).
  return PLAN_TIERS.find((t) => t.planId === planId)?.tier ?? null;
}

/** The tier above `current`. No plan -> the cheapest tier. Top tier -> null. */
export function nextTier(current: PlanTier | null | undefined): PlanTier | null {
  const i = tierRank(current);
  return TIER_ORDER[i + 1] ?? null;
}

/**
 * The plan to offer: the cheapest tier that includes `feature` when it is above
 * the current plan, else the next tier up from the current plan. Null when the
 * org is already on the top tier and nothing is higher.
 */
export function targetTierFor(opts: { feature?: GatedFeature | null; currentTier?: PlanTier | null }): PlanTier | null {
  const current = opts.currentTier ?? null;
  const required = opts.feature ? requiredPlanFor(opts.feature) : null;
  if (required && tierRank(required) > tierRank(current)) return required;
  return nextTier(current);
}

/** Rows grouped one-per-plan-name, in tier order, shaped as orderPlanCatalog's PlanBase. */
export function groupPlanRows(rows: PlanRow[] | null | undefined): Array<PlanBase & { tier: PlanTier }> {
  const out = new Map<PlanTier, PlanBase & { tier: PlanTier }>();
  for (const row of rows ?? []) {
    const tier = tierOfPlanName(row.name);
    if (!tier) continue;
    const name = PLAN_TIERS.find((t) => t.tier === tier)!.label;
    const price = Number(row.basePrice);
    if (!Number.isFinite(price) || price <= 0) continue;
    const base = out.get(tier) ?? {
      id: tier, tier, name, monthlyPrice: 0, yearlyPrice: 0, features: planCopy(name)?.features ?? [],
      monthlyPlanId: '', yearlyPlanId: '',
    };
    const yearly = /year|annual/i.test(row.billingInterval);
    // First row wins per interval (rows arrive cheapest-first per product).
    if (yearly && !base.yearlyPlanId) { base.yearlyPlanId = row.id; base.yearlyPrice = price; }
    if (!yearly && !base.monthlyPlanId) { base.monthlyPlanId = row.id; base.monthlyPrice = price; }
    out.set(tier, base);
  }
  return TIER_ORDER.filter((t) => out.has(t)).map((t) => out.get(t)!);
}

export type DirectUpgradeMode =
  /** A Stripe subscription in trial: end the trial now on this plan. */
  | 'trial'
  /** A paid Stripe subscription: change its price in place (prorated). */
  | 'change'
  /** No live subscription (free, cancelled, legacy trial): only checkout can start one. */
  | 'checkout';

/** What the sheet does, from /api/billing/upgrade-context. */
export function directUpgradeMode(ctx: {
  trialStatus?: string | null;
  currentSubscription?: { status?: string | null; stripeSubscriptionId?: string | null } | null;
} | null | undefined): DirectUpgradeMode {
  const sub = ctx?.currentSubscription;
  const subId = String(sub?.stripeSubscriptionId ?? '');
  const live = subId.startsWith('sub_');
  if (!live) return 'checkout';
  if (sub?.status === 'trialing') return 'trial';
  if (sub?.status === 'active' || sub?.status === 'past_due') return 'change';
  return 'checkout';
}

/** "$19/mo", "$180/yr" */
export function priceShort(price: number, interval: BillingInterval): string {
  return `${formatUsd(price)}/${interval === 'year' ? 'yr' : 'mo'}`;
}

/** The primary button. */
export function primaryCtaLabel(mode: DirectUpgradeMode, planName: string, price: number, interval: BillingInterval): string {
  const p = priceShort(price, interval);
  return mode === 'trial' ? `Start ${planName} now — ${p}` : `Upgrade to ${planName} — ${p}`;
}

export interface DirectOffer {
  tier: PlanTier;
  name: string;
  interval: BillingInterval;
  planId: string;
  price: number;
  /** Whether a Monthly/Annual toggle applies. */
  bothIntervals: boolean;
  /** Annual saving against 12 x monthly, 0 if none. */
  annualSaving: number;
}

/** The purchasable offer for `tier` at the wanted interval (or the one it is sold on). */
export function offerFor(
  rows: PlanRow[] | null | undefined,
  tier: PlanTier | null,
  wanted: BillingInterval,
): DirectOffer | null {
  if (!tier) return null;
  const base = groupPlanRows(rows).find((b) => b.tier === tier);
  if (!base) return null;
  const interval = intervalForPlan(base, wanted);
  const plan = interval ? planAt(base, interval) : undefined;
  if (!plan || !interval) return null;
  return {
    tier,
    name: base.name,
    interval,
    planId: plan.id,
    price: plan.price,
    bothIntervals: !isAnnualOnly(base) && !!base.monthlyPlanId && !!base.yearlyPlanId,
    annualSaving: savingsPerYear(base),
  };
}

/** The interval a plan row is billed on. */
export function intervalOfPlanId(rows: PlanRow[] | null | undefined, planId: string | null | undefined): BillingInterval | null {
  const row = (rows ?? []).find((r) => r.id === planId);
  if (!row) return null;
  return /year|annual/i.test(row.billingInterval) ? 'year' : 'month';
}

/** Outbound-first wording for the blocked feature, used as the lead benefit. */
const LEAD_BENEFIT: Partial<Record<GatedFeature, string>> = {
  voice: 'Outbound calling',
  sms: 'Outbound texting (SMS)',
};

/**
 * Three or four benefits, leading with the blocked feature (or, for a trial,
 * with what converting unlocks). The rest come from the plan's card copy.
 */
export function keyBenefits(opts: { planName: string; feature?: GatedFeature | null; mode: DirectUpgradeMode; max?: number }): string[] {
  const max = opts.max ?? 4;
  const lead: string[] = [];
  if (opts.feature) lead.push(LEAD_BENEFIT[opts.feature] ?? FEATURE_LABELS[opts.feature]);
  if (opts.mode === 'trial') {
    lead.push('Outbound calling and SMS', `The full ${opts.planName} allowances, without trial limits`);
  }
  const copy = planCopy(opts.planName);
  const rest = [...(copy ? [copy.chip] : []), ...(copy?.features ?? [])];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const b of [...lead, ...rest]) {
    const k = b.toLowerCase();
    if (seen.has(k) || Array.from(seen).some((s) => s.includes(k) || k.includes(s))) continue;
    seen.add(k);
    out.push(b);
    if (out.length >= max) break;
  }
  return out;
}

/** "You're on Growth — outbound calling is now on" */
export function successHeadline(planName: string, feature?: GatedFeature | null, mode?: DirectUpgradeMode): string {
  const lead = feature ? (LEAD_BENEFIT[feature] ?? FEATURE_LABELS[feature]) : mode === 'trial' ? 'Outbound calling' : null;
  return lead ? `You're on ${planName} — ${lead.toLowerCase()} is now on` : `You're on ${planName}`;
}
